import {
  HospitalityGapError,
  type HospitalitySource,
} from './hospitalitySource';
import type {
  AvailabilitySlot,
  Bill,
  Booking,
  CreateBookingRequest,
  Menu,
  Orderability,
  RestaurantSite,
  SlotQuery,
} from './wire';
import type {
  AddLineInput,
  Cart,
  CheckoutInput,
  DinerFulfilmentType,
  PlacedOrder,
  SetFulfilmentInput,
} from './orderWire';

/**
 * The `HospitalitySource` the restaurant blocks read through — the hospitality
 * sibling of `createStorefrontCommerceSource`.
 *
 * <h3>Why it talks to this app and not to the backend</h3>
 *
 * <p>`site()` goes to this app's own `/api/storefront/hospitality/site`, which
 * adds `?domain=<host>` on the server from the request's own `Host` header. The
 * browser names no tenant, no channel and no domain: it asks "the restaurant
 * whose site I am on", and only the server knows which that is. Routing through
 * the BFF is what makes that structurally impossible to get wrong, rather than
 * merely discouraged — and it keeps `SORSY_API_URL` out of the client bundle.
 *
 * <h3>Every implemented method goes through the BFF, and `bill` still throws</h3>
 *
 * <p>`site`, `menu`, `orderable`, `slots`, `createBooking` and
 * `bookingByNumber` each call this app's own `/api/storefront/hospitality/**`
 * handler, which adds `?domain=<host>` on the server from the request's own
 * `Host` header and forwards to the anonymous `SiteWs` / `PublicMenuWs` /
 * `PublicAvailabilityWs` / `PublicBookingWs` resources. The browser names no
 * tenant, no channel and no domain — and `menu()` takes no channel id at all,
 * because the backend resolves one from the hostname anyway.
 *
 * <h3>The three booking methods carry a refusal, not just a status</h3>
 *
 * <p>A diner whose slot went while they typed their phone number, a channel
 * that takes a deposit this site has no way to collect, a restaurant whose
 * channel has no turn time configured — each is a refusal the diner is entitled
 * to read, and each arrives as a {@link HospitalityRefusal} carrying the
 * phase-1 envelope's `errorKey` and `message`. {@link HospitalityRequestError}
 * would have flattened all three into a status code, which is why the booking
 * calls do not use it.
 *
 * <p>`createBooking` sends six fields and no ids: covers, at, name, phone,
 * email, note. `CreateBookingRequest.channelId`, `.locationId`, `.resourceIds`,
 * `.depositAmount` and `.noShowFee` exist for the till and are dropped here —
 * the resolved site owns the first two, the diary allocates tables itself, and
 * money is never something an anonymous caller names.
 *
 * <p>`BillWs` has no anonymous mapping: it lives at
 * `/{tenantId}/commerce/hospitality/bills/**` behind `COMPANY_ADMIN` /
 * `ORGANISATION_ADMIN`, so {@link HospitalitySource.bill} throws a
 * {@link HospitalityGapError} naming the resource and the public endpoint that
 * would close it.
 *
 * <p>The alternative — having this app hold a tenant id and compose the
 * tenant-scoped URL in the browser — is refused twice over: it would put a
 * tenant id in a URL an anonymous visitor's client builds, which is the exact
 * hole `StorefrontWsPaths` exists to close, and the admin permission check
 * would reject the call anyway. A named throw is a gap a build can see; a call
 * that 403s in production is one it cannot.
 *
 * <p>`HOSPITALITY_GAPS.menu` and `.orderable` are kept below as the record of
 * what the two endpoints had to look like, and are no longer thrown.
 */

export interface HospitalitySourceConfig {
  /** The app's base path, when it is deployed under one. */
  basePath?: string;
  /** Active BCP 47 tag. A locale is not a tenant selector, so the browser may
   *  carry it; the tenant never is. */
  locale?: string;
}

export class HospitalityRequestError extends Error {
  constructor(
    path: string,
    readonly status: number,
  ) {
    super(`hospitality request failed: ${path} (${status})`);
    this.name = 'HospitalityRequestError';
  }
}

/**
 * A cart or checkout call the backend refused.
 *
 * <p>Separate from {@link HospitalityRequestError} because these carry a
 * message meant for the diner. `ValidationException` and
 * `OrderServiceException` both arrive as a 4xx with a `{message}` envelope, and
 * "that dish is not available at this time" is the whole point of the call
 * failing. `status === 409` is the one to treat as state rather than error: the
 * cart the cookie names no longer exists.
 */
export class HospitalityOrderError extends Error {
  constructor(
    path: string,
    readonly status: number,
    /** The backend's own words, or null for a 5xx the diner must not see. */
    readonly detail: string | null,
  ) {
    super(detail ?? `hospitality order request failed: ${path} (${status})`);
    this.name = 'HospitalityOrderError';
  }
}

/**
 * A booking call refused for a reason the diner is entitled to read.
 *
 * <p>Carries the two members of the phase-1 error envelope a screen can use:
 * `errorKey`, the stable `HospitalityErrors` constant a branch is written
 * against, and `message`, the contract's own wording — the fallback whenever
 * this build has no string of its own for the key it was given. Neither is
 * invented here, and `developerMessage` never crosses the BFF.
 */
export class HospitalityRefusal extends Error {
  constructor(
    /** `BOOKING_RESOURCE_TAKEN`, `BOOKING_DEPOSIT_REQUIRED`, … or null. */
    readonly errorKey: string | null,
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'HospitalityRefusal';
  }
}

/** What a public endpoint would have to look like for each missing read, so the
 *  gap is stated once and reported the same way everywhere it surfaces. */
export const HOSPITALITY_GAPS = {
  menu: 'GET /v1/public/storefront/hospitality/menu?domain= (channel resolved from the host)',
  orderable:
    'GET /v1/public/storefront/hospitality/menu/items/{productId}/orderable?domain=' +
    ' (channel resolved from the host)',
  slots: 'GET /v1/public/storefront/hospitality/availability/slots?domain=',
  booking:
    'POST /v1/public/storefront/hospitality/bookings?domain= and' +
    ' GET /v1/public/storefront/hospitality/bookings/by-number/{n}?phoneLast4=',
  bill: 'GET /v1/public/storefront/hospitality/bills/{orderId}?domain= (a diner-facing bill view)',
} as const;

export function createHospitalitySource(
  config: HospitalitySourceConfig = {},
): HospitalitySource {
  const base = `${(config.basePath ?? '').replace(/\/+$/, '')}/api/storefront/hospitality`;

  async function get<T>(
    path: string,
    params?: URLSearchParams,
    init?: RequestInit,
  ): Promise<T | null> {
    const search = new URLSearchParams(params);
    if (config.locale) search.set('locale', config.locale);
    const qs = search.toString();
    const res = await fetch(`${base}${path}${qs ? `?${qs}` : ''}`, {
      headers: { Accept: 'application/json' },
      ...init,
    });
    // An unregistered hostname is a missing site, not a broken one. The block
    // renders its empty state; it never falls back to another tenant.
    if (res.status === 404) return null;
    if (!res.ok) throw new HospitalityRequestError(path, res.status);
    return (await res.json()) as T;
  }

  /**
   * The booking sibling of {@link get}: same BFF, same `no-store`, but a
   * non-OK response becomes a {@link HospitalityRefusal} carrying the
   * envelope's `errorKey` and `message` instead of a bare status.
   *
   * <p>No `locale` is appended. These calls answer instants and a booking
   * record, not prose — a language on the URL would only make a shared cache
   * think two identical requests were different.
   */
  async function refusable<T>(
    path: string,
    params?: URLSearchParams,
    init?: RequestInit,
  ): Promise<T | null> {
    const qs = params?.toString();
    const res = await fetch(`${base}${path}${qs ? `?${qs}` : ''}`, {
      cache: 'no-store',
      ...init,
      headers: { Accept: 'application/json', ...(init?.headers ?? {}) },
    });
    // 404 is "no such site" for a read and "no such booking" for a lookup, and
    // both are answers rather than failures. The lookup's two misses — unknown
    // number, wrong phone — are already one answer by the time they get here.
    if (res.status === 404) return null;
    if (!res.ok) {
      let body: { errorKey?: string | null; message?: string | null } | null = null;
      try {
        body = (await res.json()) as { errorKey?: string | null; message?: string | null } | null;
      } catch {
        body = null;
      }
      throw new HospitalityRefusal(
        body?.errorKey?.trim() || null,
        body?.message?.trim() || `hospitality request failed: ${path} (${res.status})`,
        res.status,
      );
    }
    return (await res.json()) as T;
  }

  /**
   * A cart mutation: JSON in, the whole cart out.
   *
   * <p>A 4xx carries the backend's own `message` — "supply an address or an
   * address ID, not both", "that dish is not available at this time" — and it
   * is thrown as a {@link HospitalityOrderError} so the block can show the
   * sentence rather than a generic failure. A 409 means the cookie names a cart
   * that is gone (expired, or already converted); the block starts a new one.
   */
  async function send<T>(path: string, body: unknown): Promise<T | null> {
    const res = await fetch(`${base}${path}`, {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      cache: 'no-store',
    });
    const payload = (await res.json().catch(() => null)) as
      | { message?: string | null }
      | null;
    if (!res.ok) {
      throw new HospitalityOrderError(path, res.status, payload?.message ?? null);
    }
    return payload as T | null;
  }

  async function post(path: string, body: unknown): Promise<Cart | null> {
    const payload = await send<{ cart: Cart | null }>(path, body);
    return payload?.cart ?? null;
  }

  return {
    async site(): Promise<RestaurantSite | null> {
      // The BFF answers the MAPPED shape, not the wire one: `readSite.ts` runs
      // `toRestaurantSite` server-side, which is also where `tenantId` is
      // dropped. One mapping point, and the browser never sees the field.
      return get<RestaurantSite>('/site');
    },

    async menu(at?: string): Promise<Menu | null> {
      // Mapped server-side too: `readMenu` runs `toMenu`, so the browser never
      // sees a JSON number where a decimal string belongs, and the section
      // order is the operator's `position` rather than a map iteration order.
      const params = new URLSearchParams();
      if (at) params.set('at', at);
      return get<Menu>('/menu', params);
    },

    async orderable(productId: number, at?: string): Promise<Orderability | null> {
      const params = new URLSearchParams({ productId: String(productId) });
      if (at) params.set('at', at);
      // Uncached at every layer: the handler answers `no-store`, and this asks
      // the browser not to reuse its own copy either. An 86'd dish that stays
      // green for a minute is an order the kitchen cannot cook.
      return get<Orderability>('/menu/orderable', params, { cache: 'no-store' });
    },

    async slots(query: SlotQuery): Promise<AvailabilitySlot[]> {
      const params = new URLSearchParams({
        // `covers` is the public endpoint's own name for it. `locationId` and
        // `channelId` are on `SlotQuery` for the till and are NOT sent: the
        // resolved site carries both, and a browser that could name one could
        // name another restaurant's.
        date: query.date,
        covers: String(query.partySize),
      });
      // Omitted rather than guessed when the operator has set no turn time. The
      // engine then answers `BOOKING_DURATION_REQUIRED` and the block asks the
      // diner to phone, which is the honest answer; ninety minutes invented here
      // would book real tables for the wrong length of time.
      if (query.durationMinutes != null) {
        params.set('durationMinutes', String(query.durationMinutes));
      }
      if (query.resourceType) params.set('resourceType', query.resourceType);
      // An unresolvable host is an empty grid, not a throw: the site state has
      // already said so, and a second error on top of it says nothing new.
      return (await refusable<AvailabilitySlot[]>('/slots', params)) ?? [];
    },

    async createBooking(request: CreateBookingRequest, idempotencyKey: string): Promise<Booking> {
      const booking = await refusable<Booking>('/bookings', undefined, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          // `Booking` has no `clientRef` column, so this header is the whole of
          // the idempotency mechanism. The caller mints it once per submission
          // attempt and reuses it across that attempt's retries — which is why
          // it is a parameter and not something generated in here.
          'Idempotency-Key': idempotencyKey,
        },
        body: JSON.stringify({
          covers: request.partySize,
          at: request.startAt,
          name: request.contactName,
          phone: request.contactPhone,
          email: request.contactEmail?.trim() || null,
          note: request.note?.trim() || null,
        }),
      });
      // A create that resolved no site created nothing. Answering `null` would
      // let a caller read a missing booking as a made one.
      if (!booking) {
        throw new HospitalityRefusal(
          'SITE_UNKNOWN_DOMAIN',
          'this address is not set up to take bookings',
          404,
        );
      }
      return booking;
    },

    async bookingByNumber(bookingNumber: string, phoneLast4: string): Promise<Booking | null> {
      // `null` is the ONE answer for both misses: an unknown booking number and
      // a wrong last-four. A distinct "wrong phone" would confirm that the
      // number is real, which is the whole of what an enumeration attack needs.
      return refusable<Booking>(
        `/bookings/${encodeURIComponent(bookingNumber)}`,
        new URLSearchParams({ phoneLast4 }),
      );
    },

    async bill(orderId: number): Promise<Bill | null> {
      void orderId;
      throw new HospitalityGapError('bill', HOSPITALITY_GAPS.bill);
    },

    // ---------------------------------------------------------------------
    // Ordering. These are implemented, not gapped: `CartResource` and
    // `CheckoutResource` are anonymous and host-resolved (see `orderWire.ts`
    // for the path prefix, which is NOT `StorefrontWsPaths.ROOT`). Every one
    // of them goes through this app's own `/api/storefront/hospitality/order/**`
    // handlers, which hold the cart token in an `HttpOnly` cookie — the browser
    // has no cart id, no token and no tenant, only "my cart".
    // ---------------------------------------------------------------------

    async cart(): Promise<Cart | null> {
      const body = await get<{ cart: Cart | null }>('/order/cart', undefined, {
        cache: 'no-store',
      });
      return body?.cart ?? null;
    },

    async startCart(fulfilmentType?: DinerFulfilmentType): Promise<Cart | null> {
      return post('/order/cart', { action: 'create', fulfilmentType });
    },

    async addLine(input: AddLineInput): Promise<Cart | null> {
      return post('/order/cart', { action: 'addLine', ...input });
    },

    async setLineQuantity(lineId: number, quantity: string): Promise<Cart | null> {
      return post('/order/cart', { action: 'setQuantity', lineId, quantity });
    },

    async removeLine(lineId: number): Promise<Cart | null> {
      return post('/order/cart', { action: 'removeLine', lineId });
    },

    async setFulfilment(input: SetFulfilmentInput): Promise<Cart | null> {
      return post('/order/cart', { action: 'setFulfilment', ...input });
    },

    async checkout(input: CheckoutInput, idempotencyKey: string): Promise<PlacedOrder | null> {
      const body = await send<{ order: PlacedOrder | null }>('/order/checkout', {
        ...input,
        idempotencyKey,
      });
      return body?.order ?? null;
    },
  };
}
